import { motion } from 'framer-motion';
import type { CardInstance, CardDefinition, Faction } from '../../types/kotch';
import { CARD_DEFINITIONS } from '../../data/kotch/cards';

// Legacy card art (fallback for no-faction games)
import SpearmenArt from './Spearmen.jpg';
import ArchersArt from './Archers.jpg';
import RidersArt from './Riders.jpg';
import MithraArt from './Mithra.jpg';

// Faction basic troop art
import HydariaArcherArt from './CardArt/hydaria_archer.png';
import HydariaRiderArt from './CardArt/hydaria_rider.png';
import HydariaSpearmanArt from './CardArt/hydaria_spearman.png';
import MigoraArcherArt from './CardArt/migora_archer.png';
import MigoraRiderArt from './CardArt/migora_rider.png';
import MigoraSpearmanArt from './CardArt/migora_spearman.png';
import MotraArcherArt from './CardArt/motra_archer.png';
import MotraRiderArt from './CardArt/motra_rider.png';
import MotraSpearmanArt from './CardArt/motra_spearman.png';
import RigataArcherArt from './CardArt/rigata_archer.png';
import RigataRiderArt from './CardArt/rigata_rider.png';
import RigataSpearmanArt from './CardArt/rigata_spearman.png';
import VeraArcherArt from './CardArt/vera_archer.png';
import VeraRiderArt from './CardArt/vera_rider.png';
import VeraSpearmanArt from './CardArt/vera_spearman.png';

// Knight art
import MithraKnightArt from './CardArt/migora_mithra.png';
import ReyazArt from './CardArt/migora_reyaz.png';
import BerjotArt from './CardArt/migora_berjot.png';
import HaflerArt from './CardArt/migora_hafler.png';
import AlonArt from './CardArt/migora_alon.png';
import EleiaArt from './CardArt/migora_eleia.png';
import LysiacArt from './CardArt/hydaria_lysiac.png';
import LysiacBattlemasterArt from './CardArt/hydaria_lysiac_battlemaster.png';
import RagosArt from './CardArt/hydaria_ragos.png';
import FarkionKingArt from './CardArt/motra_farkion_king.png';
import FarkionCowardArt from './CardArt/motra_farkion_coward.png';
import ArogArt from './CardArt/rigata_arog.png';
import RenedilArt from './CardArt/rigata_renedil.png';
import AverilArt from './CardArt/rigata_averil.png';
import QuitusArt from './CardArt/rigata_quitus.png';
import GimoraArt from './CardArt/rigata_gimora.png';

// Faction-specific basic troop art: FACTION_BASIC_ART[faction][type]
const FACTION_BASIC_ART: Record<Faction, Record<string, string>> = {
  Migora:  { spearman: MigoraSpearmanArt,   archer: MigoraArcherArt,   rider: MigoraRiderArt },
  Hydaria: { spearman: HydariaSpearmanArt,  archer: HydariaArcherArt,  rider: HydariaRiderArt },
  Motra:   { spearman: MotraSpearmanArt,    archer: MotraArcherArt,    rider: MotraRiderArt },
  Rigata:  { spearman: RigataSpearmanArt,   archer: RigataArcherArt,   rider: RigataRiderArt },
  Vera:    { spearman: VeraSpearmanArt,     archer: VeraArcherArt,     rider: VeraRiderArt },
};

// Knight art mapped by card name
const KNIGHT_ART: Record<string, string> = {
  'Mithra':                    MithraKnightArt,
  'Reyaz':                     ReyazArt,
  'Berjot':                    BerjotArt,
  'Hafler':                    HaflerArt,
  'Archralm Alon':             AlonArt,
  'Eleia':                     EleiaArt,
  'Armsmaster Lysiac':         LysiacArt,
  'Battlemaster Lysiac':       LysiacBattlemasterArt,
  'Ragos':                     RagosArt,
  'Stone Sovereign Farkion':   FarkionKingArt,
  'Councilor Farkion':         FarkionCowardArt,
  'Arog':                      ArogArt,
  'Renedil':                   RenedilArt,
  'Averil':                    AverilArt,
  'Quietus':                   QuitusArt,
  'Gimora':                    GimoraArt,
};

// Legacy fallback art for no-faction games
const LEGACY_ART: Record<string, string> = {
  spearman: SpearmenArt,
  archer: ArchersArt,
  rider: RidersArt,
  Mithra: MithraArt,
};

export function getCardArt(definition: CardDefinition, playerFaction?: Faction | null): string | null {
  // 1. Named card (knight) - check KNIGHT_ART by name
  if (definition.name && KNIGHT_ART[definition.name]) {
    return KNIGHT_ART[definition.name];
  }

  // 2. Basic troop with known faction - use faction-specific art
  if (playerFaction && FACTION_BASIC_ART[playerFaction]?.[definition.type]) {
    return FACTION_BASIC_ART[playerFaction][definition.type];
  }

  // 3. Legacy fallback (old JPGs) for no-faction games
  if (definition.name && LEGACY_ART[definition.name]) {
    return LEGACY_ART[definition.name];
  }
  if (LEGACY_ART[definition.type]) {
    return LEGACY_ART[definition.type];
  }

  return null;
}

interface CardProps {
  card: CardInstance;
  faceUp?: boolean;
  selected?: boolean;
  disabled?: boolean;
  revealed?: boolean;  // Card is revealed to opponent (from lane reward)
  seenByOpponent?: boolean;  // Opponent has seen this card
  onClick?: () => void;
  onHover?: (def: CardDefinition | null) => void;
  size?: 'small' | 'medium' | 'large';
  playerFaction?: Faction | null;  // Owning player's faction for basic troop art
}

export default function Card({
  card,
  faceUp = true,
  selected = false,
  disabled = false,
  revealed = false,
  seenByOpponent = false,
  onClick,
  onHover,
  size = 'medium',
  playerFaction,
}: CardProps) {
  const definition = CARD_DEFINITIONS[card.definitionId];

  if (!definition) return null;

  const sizeStyles = {
    small: { width: 70, height: 98 },
    medium: { width: 100, height: 140 },
    large: { width: 130, height: 182 },
  };

  // If revealed, always show face up (it's been revealed to opponent)
  const showFaceUp = faceUp || revealed;

  return (
    <motion.div
      className={`kotch-card ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${revealed ? 'revealed' : ''} ${seenByOpponent ? 'seen-by-opponent' : ''}`}
      style={sizeStyles[size]}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onHover ? () => onHover(definition) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      whileHover={disabled ? {} : { y: -4 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      {showFaceUp ? (
        <CardFront definition={definition} playerFaction={playerFaction} />
      ) : (
        <CardBack category={definition.category} />
      )}
    </motion.div>
  );
}

function CardFront({ definition, playerFaction }: { definition: CardDefinition; playerFaction?: Faction | null }) {
  const hasAbility = definition.ability && definition.type === 'knight';
  const cardArt = getCardArt(definition, playerFaction);

  return (
    <div className={`kotch-card-face kotch-card-front ${definition.type} ${definition.faction ? `faction-${definition.faction.toLowerCase()}` : ''} ${cardArt ? 'has-art' : ''}`}>
      {cardArt && (
        <div className="kotch-card-art" style={{ backgroundImage: `url(${cardArt})` }} />
      )}
      <div className="kotch-card-overlay">
        <span className="kotch-card-power">{definition.power}</span>
        <span className="kotch-card-type-name">{definition.type}</span>
        {definition.name && (
          <span className="kotch-card-name">{definition.name}</span>
        )}
        {definition.faction && (
          <span className="kotch-card-faction">{definition.faction}</span>
        )}
      </div>
      {hasAbility && (
        <div className="kotch-card-ability-tooltip">
          <span className="ability-name">{definition.ability!.name}</span>
          <span className="ability-description">{definition.ability!.description}</span>
        </div>
      )}
    </div>
  );
}

function CardBack({ category }: { category: 'basic' | 'special' }) {
  return (
    <div className={`kotch-card-face kotch-card-back ${category}`} />
  );
}

// Mini card for opponent hand display
export function MiniCard({ isSpecial = false }: { isSpecial?: boolean }) {
  return (
    <div className={`kotch-opponent-card ${isSpecial ? 'special' : ''}`} />
  );
}
